import { getTenantCustomization } from '@customize/registry';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type { ServiceType } from '@shared/providers/theme/types';
import { medixMenuA2, medixMenuR2 } from './medix';
import { isMenuSection, type MenuDefinition, type MenuEntry, type MenuGroup } from './types';

/**
 * Clinic-portal menu resolution.
 *
 * Order: tenant menu from `customize/` -> default (Medix) menu for the service
 * type. Family-restricted entries are then dropped, so visibility lives on the
 * entry rather than in a filter that has to know tenant names.
 */

export interface MenuContext {
  siteType?: string;
  serviceType: ServiceType;
  /** `clinic.parent_site_type` ?? `clinic.site_type`. */
  family?: string;
}

const isVisibleToFamily = (families: string[] | undefined, family: string | undefined): boolean =>
  !families || (!!family && families.includes(family));

const filterEntries = (entries: MenuEntry[], family: string | undefined): MenuEntry[] =>
  entries.filter(entry => isMenuSection(entry) || isVisibleToFamily(entry.families, family));

const filterGroups = (groups: MenuGroup[], family: string | undefined): MenuGroup[] =>
  groups
    .filter(group => isVisibleToFamily(group.families, family))
    .map(group => (group.items ? { ...group, items: filterEntries(group.items, family) } : group));

/** Apply family visibility rules to a whole menu definition. */
export function filterMenuByFamily(
  definition: MenuDefinition,
  family: string | undefined
): MenuDefinition {
  return {
    tabs: definition.tabs.map(tab => ({ ...tab, groups: filterGroups(tab.groups, family) })),
  };
}

/** The menu to render in the clinic portal for this context. */
export function getClinicMenu(context: MenuContext): MenuDefinition {
  const tenantMenu = getTenantCustomization(context.siteType)?.getClinicMenu?.({
    serviceType: context.serviceType,
    family: context.family,
  });

  const definition =
    tenantMenu ?? (context.serviceType === SERVICE_TYPE_A2 ? medixMenuA2 : medixMenuR2);

  return filterMenuByFamily(definition, context.family);
}

export * from './types';
